import { DefaultButton, PrimaryButton, SelectionMode, Stack, StackItem, Text } from '@fluentui/react';
import { useConst } from '@fluentui/react-hooks';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NotificationsTarget, useNotificationsManager } from '../../../contexts/notificationContext';
import { DataOperationIntervenantEXTPersonne } from '../../../data/DataOperationIntervenantEXTPersonne';
import { Immocom } from '../../../specifique/immocom';
import ComboBoxCategorieIntervenantEXTPersonne from '../../ComboBox/ComboBoxCategorieIntervenantEXTPersonne';
import ComboBoxIntervenantEXTPersonne from '../../ComboBox/ComboBoxIntervenantEXTPersonne';
import ComboBoxIntervenantEXTSociete from '../../ComboBox/ComboBoxIntervenantEXTSociete';
import { EUIDetailsList } from '../../EUI/EUIDetailsList';
import { EUIDialog } from '../../EUI/EUIDialog';
import EUIIconButton from '../../EUI/EUIIconButton';
import FicheIntervenantEXTPersonne from '../../Fiche/FicheIntervenantEXTPersonne';

// KP - 09/02/2024 : vu avec SL, on tente de simplifier en n'autorisant que les intervenants de type Personne

const BlocIntervenants = (props) => {

    // CONTEXTS
    const { dismiss, extract, load } = useNotificationsManager();

    // PROPS
    const {
        idOperation = undefined,
    } = props;

    // CONSTANTES
    const _styles = useConst({
        button: { root: { alignSelf: "start", marginTop: "8px !important" } },
        form: { root: { borderLeft: "1px solid #dee2e6", padding: "10px 20px" } },
        title: { root: { color: "red", fontSize: 15, textTransform: "uppercase" } }
    });

    // STATES
    const [_activeItems, _setActiveItems] = useState([]);
    const [_items, _setItems] = useState([]);

    const [_afficherDialogRetraitIntervenant, _setAfficherDialogRetraitIntervenant] = useState(false);
    const [_afficherPanelIntervenantEXTPersonne, _setAfficherPanelIntervenantEXTPersonne] = useState(false);
    const [_idCategorieIntervenantEXTPersonne, _setIdCategorieIntervenantEXTPersonne] = useState();
    const [_idIntervenantEXTPersonne, _setIdIntervenantEXTPersonne] = useState();
    const [_idIntervenantEXTSociete, _setIdIntervenantEXTSociete] = useState();
    const [_stamps, _setStamps] = useState({}); // Utilisé pour forcer la mise à jour des données dans les ComboBoxes

    // REFS
    const _isMounted = useRef(false); // Utilisé pour bloquer certains useEffect au premier render

    // MEMOS
    const _activeItem = useMemo(() => {
        if (Array.isArray(_activeItems) && _activeItems.length === 1) return _activeItems[0];
        else return {};
    }, [_activeItems])

    // METHODS
    const _chargementDonnees = useCallback(() => {
        const id = load("Chargement des intervenants en cours...");
        DataOperationIntervenantEXTPersonne._lister({ idOperation }).then(response => {
            const onSuccess = () => {
                const listIntervenantEXTPersonne = response.reponseAPIobjet;
                _setItems(listIntervenantEXTPersonne);
            };
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [idOperation]);

    const _ajouterIntervenant = useCallback(() => {
        const id = load("Ajout de l'intervenant en cours...");
        DataOperationIntervenantEXTPersonne._enregistrer(idOperation, _idIntervenantEXTPersonne).then(response => {
            const onSuccess = _chargementDonnees;
            const onError = (result) => extract(result, NotificationsTarget.main);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [_idIntervenantEXTPersonne, idOperation]);

    const _retirerIntervenants = useCallback(() => {
        const id = load("Retrait de l'intervenant en cours...");
        const { idIntervenantEXTPersonne } = _activeItem;
        DataOperationIntervenantEXTPersonne._supprimer(idOperation, idIntervenantEXTPersonne).then(response => {
            const onSuccess = _chargementDonnees;
            const onError = (result) => extract(result, NotificationsTarget.main);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [_activeItem, idOperation]);

    const _mailTo = useCallback(() => {
        if (!Array.isArray(_activeItems)) return;
        else {
            const itemsWithoutEmail = _activeItems.filter(item => !item.email);
            if (itemsWithoutEmail.length > 0) {
                const list = itemsWithoutEmail.reduce((memo, intervenant) => {
                    const { categorie, nom } = intervenant;
                    return `${memo}\r\n${categorie} : ${nom}`;
                }, "");
                return `Mail non défini pour : ${list}`;
            }
            else {
                const emails = _activeItems.map(item => item.email);
                const href = `mailto:${emails.join(";")}`;
                window.location.href = href;
            }
        }
    }, [_activeItems]);

    const _toggleUI = useCallback((nomUI, isOpen, items, callback) => () => {
        switch (nomUI) {
            case "DialogRetraitIntervenant": _setAfficherDialogRetraitIntervenant(isOpen); break;
            case "PanelIntervenantEXTPersonne": _setAfficherPanelIntervenantEXTPersonne(isOpen); break;
            default: break;
        }
        if (!!items) _setActiveItems(items);
        if (!!callback) callback();
    }, []);

    // EFFECTS (ComponentDidUpdate)
    // Recharger les données lorsqu'une dépendance change
    useEffect(() => {
        if (_isMounted.current)
            _chargementDonnees();
    }, [_isMounted, idOperation]);

    // EFFECTS (ComponentDidMount)
    useEffect(() => {
        _chargementDonnees();
        _isMounted.current = true;
    }, []);

    // UI
    const _columns = useMemo(
        () => {
            return [{
                key: "categorieIntervenantEXTPersonne",
                maxWidth: 150,
                minWidth: 150,
                name: "Catégorie",
            }, {
                key: "civilite",
                maxWidth: 50,
                minWidth: 50,
                name: "Civilité",
            }, {
                key: "prenom",
                maxWidth: 150,
                minWidth: 150,
                name: "Prénom",
            }, {
                key: "nom",
                name: "Nom",
            }, {
                key: "email",
                maxWidth: 175,
                minWidth: 175,
                name: "Email",
            }, {
                key: "tel",
                maxWidth: 100,
                minWidth: 100,
                name: "Téléphone",
            }, {
                ariaLabel: "Modifier l'intervenant",
                isIconOnly: true,
                key: "edit",
                onRender: (item) => <EUIIconButton
                    ariaLabel="Modifier l'intervenant"
                    iconProps={{ iconName: "Edit" }}
                    onClick={() => {
                        _setIdCategorieIntervenantEXTPersonne(item.idCategorieIntervenant);
                        _setIdIntervenantEXTSociete(item.idIntervenantEXTSociete);
                        _setIdIntervenantEXTPersonne(item.idIntervenantEXTPersonne)
                        _toggleUI("PanelIntervenantEXTPersonne", true)();
                    }}
                    title="Modifier l'intervenant"
                />
            }, {
                ariaLabel: "Retirer l'intervenant",
                isIconOnly: true,
                key: "remove",
                onRender: (item) => <EUIIconButton
                    ariaLabel="Retirer l'intervenant"
                    iconProps={{ iconName: "Delete" }}
                    onClick={_toggleUI("DialogRetraitIntervenant", true, [item])}
                    title="Retirer l'intervenant"
                />
            }];
        },
        []
    );

    const _menuItems = useMemo(
        () => {
            return [{
                disabled: _activeItems.length <= 0,
                iconProps: { iconName: "Mail" },
                key: "envoiMailIntervenant",
                name: "Mail",
                onClick: _mailTo
            }];
        },
        [_activeItems]
    );

    // RENDER
    return <Fragment>

        <Stack horizontal verticalFill>

            <StackItem grow>
                <EUIDetailsList
                    columns={_columns}
                    getKey={item => item.idActualite}
                    items={_items}
                    menuItems={_menuItems}
                    onSelectionChanged={(items) => _setActiveItems(items)}
                    selectionMode={SelectionMode.multiple}
                />
            </StackItem>

            <Stack styles={_styles.form}>

                <Text styles={_styles.title}>Gestion des intervenants</Text>

                <ComboBoxCategorieIntervenantEXTPersonne
                    label="Catégorie"
                    onChange={(event, option) => {
                        _setIdCategorieIntervenantEXTPersonne(option.key)
                        _setIdIntervenantEXTPersonne(undefined);
                    }}
                    selectedKey={_idCategorieIntervenantEXTPersonne}
                    stamp={_stamps.categorieIntervenantEXTPersonne}
                />

                <ComboBoxIntervenantEXTSociete
                    label="Société"
                    onChange={(event, option) => _setIdIntervenantEXTSociete(option.key)}
                    selectedKey={_idIntervenantEXTSociete}
                    stamp={_stamps.intervenantEXTSociete}
                />

                <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
                    <StackItem grow>
                        <ComboBoxIntervenantEXTPersonne
                            idCategorieIntervenantEXTPersonne={_idCategorieIntervenantEXTPersonne}
                            idIntervenantEXTSociete={_idIntervenantEXTSociete}
                            label="Intervenant"
                            onChange={(event, option) => _setIdIntervenantEXTPersonne(option.key)}
                            required
                            selectedKey={_idIntervenantEXTPersonne}
                            stamp={_stamps.intervenantEXTPersonne}
                        />
                    </StackItem>
                    <EUIIconButton
                        iconProps={{ iconName: "Add" }}
                        onClick={() => {
                            if (!!_idIntervenantEXTPersonne)
                                _setIdIntervenantEXTPersonne(undefined);
                            _toggleUI("PanelIntervenantEXTPersonne", true)();
                        }}
                        title="Créer intervenant (personne)"
                    />
                    <EUIIconButton iconProps={{ iconName: "Edit" }} title="Modifier intervenant (personne)" disabled={!_idIntervenantEXTPersonne} onClick={_toggleUI("PanelIntervenantEXTPersonne", true)} />
                </Stack>

                <PrimaryButton
                    disabled={!_idIntervenantEXTPersonne}
                    onClick={_ajouterIntervenant}
                    styles={_styles.button}
                    text="Lier l'intervenant à l'opération"
                />

            </Stack>

        </Stack>

        <FicheIntervenantEXTPersonne
            idCategorieIntervenant={_idCategorieIntervenantEXTPersonne}
            idIntervenantEXTPersonne={_idIntervenantEXTPersonne}
            idIntervenantEXTSociete={_idIntervenantEXTSociete}
            isOpen={_afficherPanelIntervenantEXTPersonne}
            onDismiss={_toggleUI("PanelIntervenantEXTPersonne", false)}
            onUpdateListCategorieIntervenantEXTPersonne={() => _setStamps(prev => ({ ...prev, categorieIntervenantEXTPersonne: Date.now() }))}
            onUpdateListIntervenantEXTSociete={() => _setStamps(prev => ({ ...prev, intervenantEXTSociete: Date.now() }))}
            onSave={() => {
                _toggleUI("PanelIntervenantEXTPersonne", false)();
                _setStamps(prev => ({ ...prev, intervenantEXTPersonne: Date.now() }));
            }}
        />

        <EUIDialog
            buttons={[
                <PrimaryButton key="delete" onClick={_toggleUI("DialogRetraitIntervenant", false, null, _retirerIntervenants)} text="Confirmer" />,
                <DefaultButton key="cancel" onClick={_toggleUI("DialogRetraitIntervenant", false)} text="Annuler" />
            ]}
            isOpen={_afficherDialogRetraitIntervenant}
            message="Confirmez-vous le retrait de cet intervenant de l'opération sélectionnée ?"
            onDismiss={_toggleUI("DialogRetraitIntervenant", false)}
            titre="Retrait d'un intervenant"
        />

    </Fragment>;

};

export default BlocIntervenants;